import { useSetAtom } from "jotai";
import { useCallback } from "react";
import {
  zslExecutionFailedErrorMsgAtom,
  showZslFeedbackAtom,
} from "@zapier/canvas-state";

import { renderZslToCanvasCommand } from "../atoms/renderZslToCanvasCommand";
import { outlineAtom } from "../atoms/outlineAtom";
import { aiPromptAtom } from "../atoms/aiPromptAtom";
import { useZslPieceCompletion } from "./useZslPieceCompletion";
import {
  promptToZslErrorAtom,
  isPromptToZslCompleteAtom,
} from "../atoms/streamingStatusAtoms";
import { handleCloseModalCommand } from "../atoms/handleCloseModalCommand";
import { tuningPromptsAtom } from "../atoms/tuningPromptsAtom";

export function usePromptToZslToCanvas() {
  const submitZslToCanvas = useSetAtom(renderZslToCanvasCommand);
  const setStringOutline = useSetAtom(outlineAtom);
  const setPrompt = useSetAtom(aiPromptAtom);
  const setTuningPrompts = useSetAtom(tuningPromptsAtom);
  const setShowZslFeedbackAtom = useSetAtom(showZslFeedbackAtom);
  const setError = useSetAtom(promptToZslErrorAtom);
  const handleCloseModal = useSetAtom(handleCloseModalCommand);
  const setZslExecutionFailedErrorMsg = useSetAtom(
    zslExecutionFailedErrorMsgAtom,
  );
  const setIsComplete = useSetAtom(isPromptToZslCompleteAtom);

  const { handleSubmit } = useZslPieceCompletion({
    api: "/api/zsl/v2/combined_stream",
    async onComplete() {
      await submitZslToCanvas();
      setIsComplete(true);
      setShowZslFeedbackAtom(true);
      setTimeout(handleCloseModal, 5000);
    },
    onError(error: string) {
      setError(error);
    },
  });

  const onPromptSubmit = useCallback(
    async (localPrompt: string) => {
      setIsComplete(false);
      setStringOutline(""); // When user modify the prompt
      setZslExecutionFailedErrorMsg(null); // Clear out old error alert
      setPrompt(localPrompt);
      setTuningPrompts([]); // Reset tuning prompts for each new AI suggestion
      await handleSubmit(localPrompt);
    },
    [
      handleSubmit,
      setZslExecutionFailedErrorMsg,
      setIsComplete,
      setPrompt,
      setStringOutline,
      setTuningPrompts,
    ],
  );

  return {
    onPromptSubmit,
  };
}
